/*
 * Hero
 */
class BlogHero extends HTMLElement {
  constructor() {
    super();

    this.heading = this.querySelector('.blog-hero__title > span');
    // console.log(document.body.offsetHeight);

    this.setup();
  }

  setup() {
    this.setSize();
  }

  setSize() {
    const width = window.innerWidth;
    const headingWidth = this.heading.offsetWidth;
    const fontSize = parseInt(window.getComputedStyle(this.heading).fontSize);
    const vw = (((width / headingWidth) * fontSize) / width) * 100;

    this.heading.style.fontSize = `${vw}vw`;
  }
}

customElements.define('blog-hero', BlogHero);

/*
 * Featured Articles
 */
class BlogFeaturedArticles extends HTMLElement {
  constructor() {
    super();

    this.autoChangeTimeout = 5000;

    this.slideWrapper = this.querySelector('.blog-featured__content-slides');
    this.slides = this.querySelectorAll('.blog-featured__content-slide');

    this.buttons = this.querySelectorAll('.blog-featured__button');

    this.primaryImages = this.querySelectorAll('.blog-featured__image a');
    this.secondaryImages = this.querySelectorAll('.blog-featured__image--secondary a');

    this.currentIndex = 0;
    this.timeout = null;

    if (this.slides.length === 0) {
      return;
    }

    this.initCarousel();

    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.gotoSlide(index);
      });
    });
  }

  initCarousel() {
    const firstSlide = this.slides[0];

    this.slideWrapper.style.height = `${firstSlide.clientHeight}px`;

    this.timeout = setTimeout(() => {
      this.gotoSlide(1);
    }, this.autoChangeTimeout);
  }

  gotoSlide(index) {
    clearTimeout(this.timeout);

    // Change the slide
    const activeSlide = this.querySelector('.blog-featured__content-slide--active');
    const nextSlide = this.slides[index];

    activeSlide.classList.remove('blog-featured__content-slide--active');
    nextSlide.classList.add('blog-featured__content-slide--active');

    // Update the button
    const activeButton = this.querySelector('.blog-featured__button--selected');
    const nextButton = this.buttons[index];

    activeButton.classList.remove('blog-featured__button--selected');
    nextButton.classList.add('blog-featured__button--selected');

    // Update the images
    const activeImage = this.primaryImages[this.currentIndex];
    const activeSecondaryImage =
      this.secondaryImages[this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1];
    const nextImage = this.primaryImages[index];
    const nextSecondaryImage = this.secondaryImages[index === 0 ? this.slides.length - 1 : index - 1];

    // Remove all z-indexes on the images
    [...this.primaryImages, ...this.secondaryImages].forEach((image) => {
      image.style.zIndex = null;
    });

    activeImage.classList.remove('blog-featured__image-image--active');
    activeSecondaryImage.classList.remove('blog-featured__image-image--active');
    activeImage.style.zIndex = 1;
    activeSecondaryImage.style.zIndex = 1;
    nextImage.classList.add('blog-featured__image-image--active');
    nextSecondaryImage.classList.add('blog-featured__image-image--active');
    nextImage.style.zIndex = 2;
    nextSecondaryImage.style.zIndex = 2;

    // Set timeout to auto change
    this.currentIndex = index;
    const nextIndex = index === this.slides.length - 1 ? 0 : index + 1;

    this.timeout = setTimeout(() => {
      this.gotoSlide(nextIndex);
    }, this.autoChangeTimeout);
  }
}

customElements.define('blog-featured-articles', BlogFeaturedArticles);

/*
 * All Articles
 */
class BlogAllArticles extends HTMLElement {
  constructor() {
    super();

    this.articlesWrapper = this.querySelector('.blog-all__articles');
    this.articles = this.querySelectorAll('.blog-article-card');

    this.sortNewestButton = this.querySelector('.js-sort-newest');
    this.sortOldestButton = this.querySelector('.js-sort-oldest');

    this.articlesViewedText = this.querySelector('.js-articles-viewed');
    this.articlesTotalText = this.querySelector('.js-articles-total');
    this.loadMoreButton = this.querySelector('.js-load-more');

    this.blockSize = Number(this.articlesWrapper.dataset.blockLength);
    this.page = 1;

    this.sortOrder = 'newest';

    this.sortNewestButton.addEventListener('click', () => {
      this.sortArticles('newest');
    });
    this.sortOldestButton.addEventListener('click', () => {
      this.sortArticles('oldest');
    });

    this.loadMoreButton.addEventListener('click', () => {
      this.showMoreArticles();
    });

    this.filterButtons = this.querySelectorAll('.js-filter-category');
    this.currentCategory = 'all';

    this.filterButtons.forEach(btn => {
      btn.addEventListener('click', () => this.filterArticlesByCategory(btn.dataset.category));
    });
  }

  async sortArticles(order) {
    this.sortOrder = order;

    // Update the active button
    if (order === 'newest') {
      this.sortNewestButton.classList.add('is-active');
      this.sortOldestButton.classList.remove('is-active');
    } else {
      this.sortNewestButton.classList.remove('is-active');
      this.sortOldestButton.classList.add('is-active');
    }

    // Fade out article container
    this.articlesWrapper.style.opacity = 0;
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Sort the articles
    const sortedArticles = this.getSortedArticleList(order);

    this.articles.forEach((article) => {
      article.remove();
    });
    sortedArticles.forEach((article, index) => {
      if (index < this.blockSize * this.page) {
        article = this.getUnhiddenArticle(article);
      } else {
        article.classList.add('blog-article-card--hidden');
      }

      this.articlesWrapper.appendChild(article);
    });

    // Fade in the container
    this.updateLoadMoreUI(sortedArticles);
    this.articlesWrapper.style.opacity = 1;
  }

  getSortedArticleList(order) {
    const filtered = [...this.articles].filter(article => {
      if (this.currentCategory === 'all') return true;
      try {
        const categories = JSON.parse(article.dataset.category);
        return categories.some(c => this.slugify(c) === this.currentCategory);
      } catch (e) {
        return false;
      }
    });

    return filtered.sort((a, b) => {
      const aDate = new Date(a.getAttribute('data-date'));
      const bDate = new Date(b.getAttribute('data-date'));

      return order === 'newest' ? bDate - aDate : aDate - bDate;
    });
  }

  getUnhiddenArticle(article) {
    if (!article.classList.contains('blog-article-card--hidden')) {
      return article;
    }

    article.classList.remove('blog-article-card--hidden');

    const imageTemplate = article.querySelector('.js-image-template');

    if (!imageTemplate) {
      return article;
    }

    const imageContainer = article.querySelector('.blog-article-card__image');

    imageContainer.appendChild(imageTemplate.content.cloneNode(true));
    imageTemplate.remove();

    return article;
  }

  showMoreArticles() {
    const sortedArticles = this.getSortedArticleList(this.sortOrder);
    const newArticles = sortedArticles.slice(this.blockSize * this.page, this.blockSize * (this.page + 1));

    newArticles.forEach((article) => {
      article.remove();

      article = this.getUnhiddenArticle(article);
      this.articlesWrapper.appendChild(article);
    });

    this.articlesViewedText.textContent = Math.min(sortedArticles.length, this.blockSize * (this.page + 1));

    if (sortedArticles.length <= this.blockSize * (this.page + 1)) {
      this.loadMoreButton?.remove();
    }

    this.page += 1;
  }

  slugify(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  updateLoadMoreUI(sortedArticles) {
    const viewedCount = Math.min(sortedArticles.length, this.blockSize * this.page);
    this.articlesViewedText.textContent = viewedCount;
    this.articlesTotalText.textContent = sortedArticles.length;

    if (sortedArticles.length > viewedCount) {
      if (!this.loadMoreButton?.isConnected) {
        this.querySelector('.blog-all__footer')?.appendChild(this.loadMoreButton);
      }
    } else {
      this.loadMoreButton?.remove();
    }
  }

  filterArticlesByCategory(category) {
    this.currentCategory = category;
    this.page = 1;

    this.filterButtons.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.category === category);
    });

    this.sortArticles(this.sortOrder);
  }
}

customElements.define('blog-all-articles', BlogAllArticles);
